import {
  BarChart3,
  Building2,
  Database,
  LayoutTemplate,
  Network,
  Radio,
  ShieldCheck,
  TrendingUp,
  Users,
} from "lucide-react";
import React from "react";
import { PageLayout } from "../components";
import ProductOverview from "../components/product/ProductOverview";
import { useTranslation } from "../contexts/LanguageContext";
import { openInsightsSSO } from "../shared/insightsSso";

/**
 * Página comercial do Virtus Insights (plataforma separada de dashboards de KPIs).
 * O acesso é por SSO com a sessão do SMM — ver `openInsightsSSO()`.
 */
const VirtusInsights: React.FC = () => {
  const { t } = useTranslation();

  return (
    <PageLayout
      title={t("Virtus Insights")}
      subtitle={t(
        "Dashboards de KPIs para acompanhar empresas, filiais e clientes em um só lugar.",
      )}
      containerSize="full"
      showContainer={true}
      padding="none"
      className="w-full px-2! py-2!"
    >
      <ProductOverview
        icon={BarChart3}
        productName={t("Virtus Insights")}
        tagline={t(
          "Transforme os dados da operação em indicadores claros, com o dashboard certo para cada pessoa.",
        )}
        description={t(
          "O Virtus Insights reúne os dados das suas fontes em dashboards de KPIs e gráficos, organizados por empresa, filial e cliente. Cada usuário vê só os dashboards a que tem acesso, e os modelos prontos aceleram a criação de novos painéis. O acesso é feito com o mesmo login do Social Manager, sem precisar entrar de novo.",
        )}
        ctaLabel={t("Abrir Virtus Insights")}
        onCta={openInsightsSSO}
        benefitsTitle={t("Por que usar o Insights")}
        benefitsSubtitle={t("O que ele resolve na sua gestão")}
        benefits={[
          {
            icon: TrendingUp,
            title: t("Indicadores em tempo real"),
            description: t(
              "KPIs e gráficos atualizados a partir das suas fontes de dados, sem planilhas manuais.",
            ),
          },
          {
            icon: Network,
            title: t("Visão por estrutura"),
            description: t(
              "Acompanhe a empresa inteira, cada filial ou cada cliente, no nível de detalhe certo.",
            ),
          },
          {
            icon: ShieldCheck,
            title: t("Acesso controlado"),
            description: t(
              "Permissões por perfil e por dashboard: cada pessoa vê só o que precisa.",
            ),
          },
        ]}
        featuresTitle={t("Principais recursos")}
        featuresSubtitle={t("O que você encontra dentro do Insights")}
        features={[
          {
            icon: BarChart3,
            title: t("Dashboards de KPIs"),
            description: t(
              "Painéis com indicadores e gráficos para acompanhar os resultados da operação.",
            ),
          },
          {
            icon: LayoutTemplate,
            title: t("Modelos de dashboard"),
            description: t(
              "Modelos prontos para criar novos painéis com rapidez e manter o padrão entre clientes.",
            ),
          },
          {
            icon: Building2,
            title: t("Empresas, filiais e clientes"),
            description: t(
              "Estrutura em níveis, com dashboards e usuários próprios em cada um.",
            ),
          },
          {
            icon: Users,
            title: t("Usuários e permissões"),
            description: t(
              "Gestão de usuários com acesso por dashboard e registro de auditoria.",
            ),
          },
          {
            icon: Radio,
            title: t("Acesso único (SSO)"),
            description: t(
              "Entra direto com o login do Social Manager, sem precisar de outra senha.",
            ),
          },
        ]}
        flowTitle={t("Como ele se encaixa no seu fluxo")}
        flowSubtitle={t("Dos dados à decisão")}
        flow={[
          { icon: Database, label: t("Seus dados") },
          { icon: BarChart3, label: t("Virtus Insights") },
          { icon: TrendingUp, label: t("Decisões melhores") },
        ]}
      />
    </PageLayout>
  );
};

export default VirtusInsights;
